import { Eye, Printer } from 'react-feather';
import PageLayout from '../../../components/PageLayout';
import {
  formatDate,
  getItemById,
  getQueryString,
  hasExpired,
} from '../../../utils/common.util';
import useSWR from 'swr';
import { useState } from 'react';
import ReportsModal from './ReportsModal';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import { useDebounceState } from '../../../utils/useDebounceState';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import Combobox from '../../../components/combobox/Combobox';
import { useUrlState } from '../../../utils/useUrlState';
import { RenderIf } from '../../../components/RenderIf';
import ExportReportButton from '../../../components/ExportReportButton';

const initialData = {
  dailyBillingFeeId: '',
  dateFrom: '',
  dateTo: '',
  studentId: '',
  classId: '',
  genericGroupingId: '',
};

export default function DailyReconciliationReport() {
  const [isOpen, setIsOpen] = useState('');
  const [query, setQuery] = useUrlState(initialData);

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];
  const selectedFeetype = getItemById(feeTypes, query.dailyBillingFeeId);
  const isGeneric = selectedFeetype?.type === 'Generic';

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );

  const mainUrl = `daily-billing/generate-reconciliation-report`;
  const { data, isLoading, error } = useSWR(
    query.dateFrom && query.dateTo
      ? `${mainUrl}?${getQueryString(query)}`
      : null,
  );

  // const feeCollections = dailyCollectionData?.feeCollections ?? [];
  const feeCollections = data?.response?.feeGroups ?? [];
  const feeType = getItemById(feeTypes, query.dailyBillingFeeId);
  const student = getItemById(students, query.studentId);

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  const { data: classes } = useSWR('class/list');

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Daily Reconciliation Report`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Daily Reconciliation Report</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">
        Select at least, start and end dates to show report.
      </p>
      <div className={`mt-10 ${isGeneric ? 'card-row-3' : 'card-row-4'}`}>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>
        <Select
          labelText="Fee Type"
          value={query.dailyBillingFeeId}
          onChange={(e) =>
            setQuery({
              dailyBillingFeeId: e.target.value,
              classId: '',
              genericGroupingId: '',
            })
          }
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <Select
          labelText={isGeneric ? 'Group' : 'Class'}
          disabled={!query.dailyBillingFeeId}
          onChange={(e) =>
            setQuery(
              isGeneric
                ? { genericGroupingId: e.target.value, classId: '' }
                : { classId: e.target.value, genericGroupingId: '' },
            )
          }
          value={isGeneric ? query.genericGroupingId : query.classId}
        >
          <SelectItem value="" text="All" />
          {selectedFeetype?.genericAmounts?.map((item) => (
            <SelectItem key={item.id} text={item.name} value={item.id} />
          ))}
          {selectedFeetype?.classAmounts?.map((item) => (
            <SelectItem
              key={item.id}
              text={item.classDescription}
              value={item.classId}
            />
          ))}
        </Select>
        <RenderIf condition={isGeneric}>
          <Select
            labelText={'Class'}
            disabled={!query.dailyBillingFeeId}
            onChange={(e) => setQuery({ classId: e.target.value })}
            value={query.classId}
          >
            <SelectItem value="" text="All" />
            {classes?.map((term) => (
              <SelectItem
                key={term.id}
                value={term.id}
                text={term.description}
              />
            ))}
          </Select>
        </RenderIf>
        <Combobox
          options={students ?? []}
          label="Select a Student"
          // customStyles={{ width: '300px' }}
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading} // Pass the loading state down
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {feeCollections?.length > 0 && (
          <>
            <PrintOutHead
              title={`Daily Fee Reconciliation Report`}
              headings={[
                {
                  label: 'Student',
                  value:
                    (student?.firstName ?? '') +
                    ' ' +
                    (student?.lastName ?? ''),
                },
                {
                  label: 'Fee Type',
                  value: feeType?.name ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                {feeCollections.map((collection) => (
                  <CollectionSection
                    key={collection.feeType}
                    collectionData={collection}
                  />
                ))}
              </div>
            </PrintView>
          </>
        )}
        {feeCollections?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {feeCollections?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Daily Fee Reconciliation Report.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <ReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

const CollectionSection = ({ collectionData }) => {
  return (
    <div className="mt-20">
      <p>{collectionData.feeType}</p>
      <table className="bx--data-table table-white">
        <thead>
          <tr>
            <th>Date Created</th>
            <th>Payment Date</th>
            <th>Student Name</th>
            <th>Class</th>
            <th>Billing</th>
            <th>Collection</th>
            <th>Deposit Allocated</th>
            <th>Arrears</th>
            <th>Arrears Paid</th>
          </tr>
        </thead>
        <tbody>
          {collectionData.studentRecords.map((entry) => {
            return (
              <tr key={entry.id}>
                <td>{formatDate(entry.date)}</td>
                <td>{formatDate(entry.paymentDate)}</td>
                <td>{entry.studentName}</td>
                <td>{entry.classDescription}</td>
                <td>{entry.billingAmount}</td>
                <td>{entry.paymentAmount}</td>
                <td>{entry.depositAmount}</td>
                <td>{entry.difference}</td>
                <td>{entry.arrearsPaymentAmount}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td colSpan="4">Total</td>
            <td>{collectionData.total.billingAmount}</td>
            <td>{collectionData.total.paymentAmount}</td>
            <td>{collectionData.total.depositAmount}</td>
            <td>{collectionData.total.difference}</td>
            <td>{collectionData.total.arrearsPaymentAmount}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
